import { Container } from '@acies/core';
import { Divider, IconRing, Label, theme } from '@acies/ui';
import { ConfiguratorModel } from '../../configurator.model';

/**
 * What went wrong the last time the position on screen was sent to the engine, as a card for the places that show it.
 *
 * One component for both places — the geometry dialog, where the drawing would be, and the properties panel, under the properties — so the two cannot say it differently. What it says is `ConfiguratorModel.currentError`, which is the selected position's own `error`; see `ModelPosition.error`.
 *
 * A card and not a dialog: it sits in the flow of whatever shows it, with no overlay and nothing dimmed behind it, since what it says belongs beside the position it is about rather than in front of everything.
 *
 * Framed with the error stroke, rounded, and filling the width it is given and the height too, up to the height of a card: under the properties it takes what the rows leave, and in the geometry dialog it sits where the drawing was.
 */
export default class PositionErrorMessage extends Container {
	#body!: Container;
	#content!: Container;
	#divider!: Divider;
	#header!: Container;
	#iconRing!: IconRing;
	#messageLabel!: Label;
	#titleLabel!: Label;

	public constructor() {
		super();
		this.width = 'fill';
		// the room it is given, up to the height of a card: under the properties it takes what the
		// rows leave, and a long message scrolls inside the content rather than growing the card past it
		this.height = 'fill';
		this.maxHeight = 416;
		this.cornerRadius = 8;
		this.stroke = theme.strokes.error;
		// opaque, so whatever it sits over — the properties, when they scroll under it — does not show through
		this.fill = theme.colors.fill.white;
		this.autoLayout = 'vertical';
		// hidden until there is something to say, so a host that adds the card has nothing on screen
		// while nothing is wrong
		this.visible = false;
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one
		this.errorChanged = this.errorChanged.bind(this);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds: a card built after a refusal had landed
		// would miss it. What the model already holds is read on connect, in initialize.
		ConfiguratorModel.currentError.subscribe(this.errorChanged);
	}

	/**
	 * The row across the top of the card, which the icon and the title go in.
	 *
	 * Fixed in height rather than hugging, so the card's top edge reads the same whatever the title says, and left-aligned with the padding and the gap a row of an icon and a label has elsewhere.
	 */
	public get header(): Container {
		if (!this.#header) {
			this.#header = new Container();
			this.#header.width = 'fill';
			this.#header.height = 72;
			this.#header.alignment = 'left';
			this.#header.paddingHorizontal = 16;
			this.#header.gapHorizontal = 16;
		}
		return this.#header;
	}

	/** The ring at the start of the header, which says at a glance that the card is about something that went wrong: the error look, around the alert icon. */
	public get iconRing(): IconRing {
		if (!this.#iconRing) {
			this.#iconRing = new IconRing();
			this.#iconRing.variant = 'error';
			this.#iconRing.name = 'alert-circle';
		}
		return this.#iconRing;
	}

	/**
	 * The title beside the ring, which says what kind of card this is.
	 *
	 * The same text whatever went wrong — what went wrong is the message below — and set up the way `Dialog` sets up its title: dark, medium, filling the width the ring leaves.
	 */
	public get titleLabel(): Label {
		if (!this.#titleLabel) {
			this.#titleLabel = new Label();
			this.#titleLabel.width = 'fill';
			this.#titleLabel.variant = 'dark';
			this.#titleLabel.size = 'medium';
			this.#titleLabel.text = 'Beregningsfejl';
		}
		return this.#titleLabel;
	}

	/** The rule under the header, which parts what kind of card this is from what went wrong. A horizontal rule as a Divider comes, since the card stacks top to bottom. */
	public get divider(): Divider {
		if (!this.#divider) {
			this.#divider = new Divider();
		}
		return this.#divider;
	}

	/**
	 * The room under the rule, which {@link PositionErrorMessage.content} scrolls inside.
	 *
	 * A container of its own around the content, for the stroke: the stroke is two pixels drawn down the middle of the card's edge, so one of them lies inside the card, and a scrollbar flush against the edge would paint over it. The padding on the right and at the bottom keeps the scrolling content, and with it the
	 * scrollbar, that much clear of the edge on the two sides a scrollbar can touch — and the same at the top, so what scrolls out at the top stops short of the rule rather than running up under it.
	 */
	public get body(): Container {
		if (!this.#body) {
			this.#body = new Container();
			this.#body.width = 'fill';
			this.#body.height = 'fill';
			this.#body.paddingTop = 4;
			this.#body.paddingRight = 4;
			this.#body.paddingBottom = 4;
		}
		return this.#body;
	}

	/**
	 * What went wrong goes in here, inside the body under the rule.
	 *
	 * A least height rather than a fixed one, since a message is as long as the engine made it: a short one leaves the card the height of a card, and a long one grows it — up to the card's own `maxHeight`, past which the message scrolls in here instead. Clipped for that, and not only for the scrollbar: a flex child
	 * that is not clipped cannot be made lower than its content, so the message would run out over the bottom of the card rather than roll inside it.
	 */
	public get content(): Container {
		if (!this.#content) {
			this.#content = new Container();
			this.#content.width = 'fill';
			this.#content.height = 'fill';
			this.#content.clipContent = true;
			this.#content.minHeight = 88;
			this.#content.paddingHorizontal = 16;
			this.#content.paddingVertical = 16;
		}
		return this.#content;
	}

	/**
	 * What went wrong, in the words it came in.
	 *
	 * Not truncated, unlike a Label elsewhere: a Label cuts itself to one line with an ellipsis, which is right for a cell and wrong for a message, where the end of the sentence is the point. With truncation off the text wraps to as many lines as it needs, and `content` grows with it.
	 *
	 * Its line breaks are kept as well. The engine writes a calculation's refusal as a list, one finding to a line, and collapsed into one run of words the list is unreadable.
	 */
	public get messageLabel(): Label {
		if (!this.#messageLabel) {
			this.#messageLabel = new Label();
			this.#messageLabel.width = 'fill';
			this.#messageLabel.truncate = false;
			this.#messageLabel.preserveLineBreaks = true;
		}
		return this.#messageLabel;
	}

	protected override initialize(): void {
		super.initialize();
		this.header.addComponents([this.iconRing, this.titleLabel]);
		this.content.addComponents([this.messageLabel]);
		this.body.addComponents([this.content]);
		this.addComponents([this.header, this.divider, this.body]);
		this.errorChanged(ConfiguratorModel.currentError.value);
	}

	/**
	 * Unsubscribes, because the model outlives the card.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a card that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentError.unsubscribe(this.errorChanged);
	}

	/**
	 * Shows what the model now holds, and nothing when it holds nothing.
	 *
	 * Hidden rather than left empty on null, so nothing on screen says something went wrong when nothing did — and the host need not know when to show it, which is the point of the card reading the model itself.
	 */
	private errorChanged(error: string | null): void {
		this.messageLabel.text = error ?? '';
		this.visible = error !== null;
	}
}
customElements.define('cf-position-error-message', PositionErrorMessage);
